import { ConnectProto } from "@racoon/api";

/**
 * The `JobCreate` fields a draft job is created with once the route is
 * submitted. The time window is left out on purpose: it follows the shift date
 * picked inside the modal, which the caller does not know yet.
 *
 * `twStartAt` is the exception for a pickup location group's drafts: it picks
 * which of the container group's schedule windows that day the stop is.
 */
interface DraftJobCreate {
  pickupLocationId: bigint;
  type: ConnectProto.JobType;
  jobCategory: ConnectProto.JobCategory;
  containerGroupId?: bigint;
  containerTypeId?: bigint;
  pickupLocationGroupId?: bigint;
  serviceProviderId?: bigint;
  serviceReceiverId?: bigint;
  authorId?: bigint;
  twStartAt?: ConnectProto.JobStop["twStartAt"];
}

/**
 * A job the dispatcher is about to create, shown in the modal before it exists
 * on the server. It is addressed by a *negative* synthetic id — every real job
 * id is positive — so trip slots, the map preview, the volume maths and the
 * truck-compatibility checks all keep working on drafts without a second code
 * path. `handleSubmit` creates them first and swaps in the real ids.
 */
export interface DraftJob {
  tempId: number;
  /** Stand-in record for display only; never came from (or goes to) the server. */
  job: ConnectProto.Job;
  create: DraftJobCreate;
  /**
   * A pickup location group's window whose job that day is routed, in progress
   * or another group's: this draft creates a duplicate of that job (bulk create
   * needs allowDuplicates).
   */
  duplicateOf?: bigint;
}

export const isDraftJobId = (jobId: number) => jobId < 0;

interface MakeDraftJobArgs {
  /** 0-based position among the drafts; decides the synthetic id. */
  index: number;
  create: DraftJobCreate;
  pickupLocation?: ConnectProto.PickupLocation;
  containerGroup?: ConnectProto.ContainerGroup;
  /** Customer the job is created for, shown on the job row. */
  serviceReceiver?: ConnectProto.Organization;
  duplicateOf?: bigint;
}

/**
 * The stand-in `Job` is assembled by hand rather than through the generated
 * schema: only our own components read it, and everything they read is set
 * here. The same shortcut the map preview's `previewRoute` takes.
 */
export const makeDraftJob = ({
  index,
  create,
  pickupLocation,
  containerGroup,
  serviceReceiver,
  duplicateOf,
}: MakeDraftJobArgs): DraftJob => {
  const tempId = -(index + 1);
  const job = {
    id: BigInt(tempId),
    pickupLocationId: create.pickupLocationId,
    pickupLocation,
    containerGroup,
    containerTypeId: create.containerTypeId,
    containerType: containerGroup?.containerType,
    serviceReceiverId: create.serviceReceiverId,
    serviceReceiver,
    serviceProviderId: create.serviceProviderId,
    pickupLocationGroupId: create.pickupLocationGroupId,
    type: create.type,
    jobCategory: create.jobCategory,
    status: ConnectProto.JobStatus.CREATED,
    dispatcherReviewStatus: ConnectProto.DispatcherReviewStatus.UNDEFINED,
  } as unknown as ConnectProto.Job;

  return { tempId, job, create, duplicateOf };
};
